import React, { useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../contexts';
import { Modal } from '../Modal';
import './SubscriptionIrregularityModal.scss';

const IRREGULAR_SUBSCRIPTION_PLAN_ID = 39;
const SPECIAL_OFFER_PATH = '/register/come-back-offer';
const SPECIAL_OFFER_QUERY = {
    utm_source: 'productioncrate',
    utm_medium: 'modal',
    utm_campaign: 'f_pro_convert',
};
const DISMISS_REGISTRY_KEY = '__pcSubscriptionIrregularityModalDismissedUsers';
const LOCAL_PREVIEW_HOSTNAMES = ['localhost', '127.0.0.1'];

const resolveSpecialOfferUrl = () => {
    if (typeof window === 'undefined') {
        return `https://user.productioncrate.com${SPECIAL_OFFER_PATH}`;
    }

    const hostname = window.location.hostname || '';
    const isLocalHost = LOCAL_PREVIEW_HOSTNAMES.includes(hostname) || hostname.endsWith('.localhost');
    const isQaHost = hostname === 'user-qa.productioncrate.com' || hostname === 'staging.productioncrate.com';
    const baseDomain = isLocalHost || isQaHost ? 'user-qa.productioncrate.com' : 'user.productioncrate.com';
    const url = new URL(`https://${baseDomain}${SPECIAL_OFFER_PATH}`);

    Object.entries(SPECIAL_OFFER_QUERY).forEach(([key, value]) => {
        url.searchParams.set(key, value);
    });

    return url.toString();
};

const getDismissRegistry = () => {
    if (typeof window === 'undefined') {
        return {};
    }

    if (!window[DISMISS_REGISTRY_KEY] || typeof window[DISMISS_REGISTRY_KEY] !== 'object') {
        window[DISMISS_REGISTRY_KEY] = {};
    }

    return window[DISMISS_REGISTRY_KEY];
};

const hasPendingTermsConsent = (user) => {
    if (!user) {
        return false;
    }

    const settings = user.settings && typeof user.settings === 'object' ? user.settings : {};
    const optInValue = settings.terms_privacy_optin;

    return !(
        optInValue === true ||
        optInValue === 'true' ||
        optInValue === 1 ||
        optInValue === '1' ||
        settings.terms_privacy_optin_at
    );
};

const SubscriptionIrregularityModal = () => {
    const { user, isAuthenticating } = useUserContext();
    const [dismissed, setDismissed] = useState(false);
    const userId = user?.id ?? null;
    const specialOfferUrl = useMemo(() => resolveSpecialOfferUrl(), []);

    useEffect(() => {
        if (!userId) {
            setDismissed(false);
            return;
        }

        setDismissed(Boolean(getDismissRegistry()[userId]));
    }, [userId]);

    const shouldDisplay = useMemo(() => {
        if (isAuthenticating) {
            return false;
        }

        if (!user || hasPendingTermsConsent(user)) {
            return false;
        }

        return Number(user?.team?.subscription?.plan?.id) === IRREGULAR_SUBSCRIPTION_PLAN_ID;
    }, [isAuthenticating, user]);

    const handleDismiss = () => {
        if (userId) {
            getDismissRegistry()[userId] = true;
        }

        setDismissed(true);
    };

    const handleOfferClick = () => {
        handleDismiss();
        window.location.href = specialOfferUrl;
    };

    if (!shouldDisplay || dismissed) {
        return null;
    }

    const message = (
        <div className="subscription-irregularity">
            <p className="subscription-irregularity__lead">
                We found some irregularities in your subscription due to technical issues.
            </p>
            <p className="subscription-irregularity__body">
                Your account will have Pro Access for <strong>2 more weeks</strong>. After that, you will be <strong>downgraded</strong> to a Free account.
            </p>
            <p className="subscription-irregularity__body subscription-irregularity__body--secondary">
                To help you renew your membership, here is a special limited-time offer at a <strong>50% discount</strong>:
            </p>
        </div>
    );

    return (
        <Modal
            visible={true}
            pretitle="Action needed"
            title="Subscription information update"
            msg={message}
            cta="Claim offer"
            ctaCallback={handleOfferClick}
            ctaClose="Dismiss"
            ctaCloseCallback={handleDismiss}
            overlayCallback={handleDismiss}
            overlayClassName="subscription-irregularity-modal__overlay"
            showCloseIcon={true}
            className="subscription-irregularity-modal"
        />
    );
};

export default SubscriptionIrregularityModal;
